﻿import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { str_isEmpty } from '@Util/Utils';

import Table_Head from '@Elements/Table/Table_Head';
import Table_Data from '@Elements/Table/Table_Data';
import dayjs from 'dayjs';

import {
    Client_Website_History_Type,
    Client_Single_Website_Table_Props
} from '@Util/Types';

export default function Client_Single_Website_Table(props: Client_Single_Website_Table_Props): JSX.Element {
    const [t] = useTranslation();

    const [sort_direction, set_sort_direction] = useState<"Ascending" | "Descending">("Ascending");
    const [active_sorting_column, set_active_sorting_column] = useState<string>("name");

    const table_data: Client_Website_History_Type[] = useMemo(() => {
        let sorted_data: Client_Website_History_Type[] = [];

        if (sort_direction == "Ascending") {
            sorted_data = props.website_data.sort((a: Client_Website_History_Type, b: Client_Website_History_Type) => a[active_sorting_column] > b[active_sorting_column] ? 1 : -1);
        } else {
            sorted_data = props.website_data.sort((a: Client_Website_History_Type, b: Client_Website_History_Type) => a[active_sorting_column] < b[active_sorting_column] ? 1 : -1);
        }

        return sorted_data;
    }, [props.website_data, sort_direction, active_sorting_column]);

    function handleSetSortingOnTableHead(col: string): void {
        if (str_isEmpty(col)) {
            throw new Error("Handle sorting on table: Column is empty");
        }

        if (col === active_sorting_column) {
            if (sort_direction == "Ascending") {
                return set_sort_direction("Descending");
            } else {
                return set_sort_direction("Ascending");
            }
        }

        set_active_sorting_column(col);
        set_sort_direction("Ascending");
    }

    function Empty_Table(): JSX.Element {
        return (
            <tr>
                <Table_Data align="left">
                    <span className="Table_Data_Customer_Name">{t('Text.NoClientWebsitesToShow')}</span>
                </Table_Data>
                <Table_Data align="right">
                    <></>
                </Table_Data>
            </tr>
        )
    }

    return (
        <section className = "Module">
            <header className="Module_Header">
                <h2 className="Headline2">{t('Text.ExceptionsAndResponseTime')}</h2>
            </header>
            <div className="Table_Wrapper">
                <table className="Table">
                    <thead>
                        <tr>
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.LastUpdated')}
                                sortingAction={() => handleSetSortingOnTableHead('dateTime')}
                                sortingDataName="dateTime"
                                style={{ width: "40%" }}
                            />
                            <Table_Head
                                align="right"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.ResponseTime')}
                                sortingAction={() => handleSetSortingOnTableHead('responseTime')}
                                sortingDataName="responseTime"
                                style={{ width: "30%" }}
                            />
                            <Table_Head
                                align="right"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Exceptions')}
                                sortingAction={() => handleSetSortingOnTableHead('exceptionCount')}
                                sortingDataName="exceptionCount"
                                style={{ width: "30%" }}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            table_data.isEmpty() ?
                                <Empty_Table /> :
                                table_data?.reverse()?.map((history_object: Client_Website_History_Type, index: number) =>
                                    <tr key={`customer_website_table_row${index}`}>
                                        <Table_Data
                                            align="left"
                                            style={{ width: "40%" }}
                                        >
                                            {dayjs(history_object.dateTime).format("YYYY-MM-DD - HH:mm")}
                                        </Table_Data>
                                        <Table_Data
                                            align="right"
                                            style={{ width: "30%" }}
                                        >
                                            {`${history_object.responseTime.toFixed(1)} ms`}
                                        </Table_Data>
                                        <Table_Data
                                            align="right"
                                            style={{ width: "30%" }}
                                        >
                                            {`${history_object.exceptionCount} ${t('Text.Pcs')}`}
                                        </Table_Data>
                                    </tr>
                                )
                        }
                    </tbody>
                </table>
            </div>
        </section>
    );
}